import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Search, Filter, Video, Image as ImageIcon, Share2 } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import YouTubeEmbed from '@/components/YouTubeEmbed';
import { toWordPreview } from '@/lib/textPreview';
import { buildShareUrl } from '@/lib/shareUrl';
import { shareContent } from '@/lib/shareContent';

const Learning = () => {
  const navigate = useNavigate();
  const { learning } = useData();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('All');
  const [shareMessage, setShareMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const categoryOptions = ['All', ...Array.from(new Set(learning.map((item) => item.category).filter(Boolean)))];
  const categoryFiltered =
    category === 'All' ? learning : learning.filter((item) => item.category === category);
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filtered = !normalizedSearch
    ? categoryFiltered
    : categoryFiltered.filter((item) => {
        const searchHaystack = [
          item.title,
          item.category,
          item.instructor,
          item.description,
          item.difficultyLevel,
          item.status,
          item.duration,
        ]
          .join(' ')
          .toLowerCase();

        return searchHaystack.includes(normalizedSearch);
      });

  const onSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedLearning = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const handleShareLearning = async (item: (typeof learning)[number]) => {
    const shareUrl = buildShareUrl("learning", item.slug || item.id);
    const shareText = `${item.title} (${item.category}) by ${item.instructor}. ${item.duration}.`;

    setShareMessage('');
    const result = await shareContent({
      title: item.title,
      text: shareText,
      url: shareUrl,
      imageUrl: item.imageUrl,
    });

    if (result.status === "copied") {
      setShareMessage('Learning link copied to clipboard.');
      return;
    }

    if (result.status === "unsupported") {
      setShareMessage("Sharing is not supported in this browser.");
      return;
    }

    if (result.status === "error") {
      setShareMessage('Unable to share this learning resource right now.');
    }
  };

  const openLearningDetails = (item: typeof learning[number]) => {
    navigate(`/learning/${item.slug || item.id}`);
  };

  return (
    <div className="min-h-screen bg-background">
      
      {/* Header */}
      <div className="hero-gradient text-primary-foreground py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8" />
            <div>
              <h1 className="font-display font-bold text-2xl md:text-3xl">
                Learning Resources
              </h1>
              <p className="text-primary-foreground/80 max-w-lg">
                Master new skills with our comprehensive learning resources.
                Access expert-led courses, tutorials, and guides.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">

        {/* Search */}
        <div className="mb-8">
          <h2 className="section-title mb-4">All Learning Resources</h2>

          <form onSubmit={onSearchSubmit} className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by title, category, instructor, level..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-input bg-background rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
            >
              <Search size={15} />
              Search
            </button>
          </form>
          {shareMessage && (
            <p className="mt-2 text-sm text-muted-foreground">{shareMessage}</p>
          )}
        </div>

        {/* Category Buttons */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {categoryOptions.map(cat => (
              <button
                key={cat}
                onClick={() => {
                  setCategory(cat);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1 rounded-md text-sm transition-colors ${
                  category === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Filter size={14} className="inline mr-1" />
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Learning Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-semibold mb-2">
              No Learning Resources Found
            </h3>
            <p className="text-muted-foreground">
              Try adjusting your search.
            </p>
          </div>
        ) : (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
            {paginatedLearning.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                viewport={{ once: true }}
                className="mb-6 break-inside-avoid"
              >
                <div
                  className="finance-card p-6 cursor-pointer"
                  role="link"
                  tabIndex={0}
                  onClick={() => openLearningDetails(item)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openLearningDetails(item);
                    }
                  }}
                >

                  {/* Title */}
                  <h3 className="font-display font-semibold text-xl text-foreground mb-2">
                    <Link to={`/learning/${item.slug || item.id}`} className="hover:text-secondary transition-colors">
                      {item.title}
                    </Link>
                  </h3>

                  {/* Difficulty + Icons */}
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        item.difficultyLevel === 'beginner'
                          ? 'bg-green-100 text-green-800'
                          : item.difficultyLevel === 'intermediate'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {item.difficultyLevel}
                    </span>

                    {item.imageUrl && (
                      <ImageIcon size={16} className="text-blue-500" />
                    )}
                    {item.videoUrl && (
                      <Video size={16} className="text-red-500" />
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                    {toWordPreview(item.description, 24)}
                  </p>

                  {/* Instructor + Duration */}
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mb-4">
                    <span>{item.instructor}</span>
                    <span>•</span>
                    <span>{item.duration}</span>
                  </div>

                  {/* Media Section */}
                  {(item.imageUrl || item.videoUrl) && (
                    <div className="mb-4 grid grid-cols-1 gap-4">
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt={item.title}
                          className="w-full h-48 object-cover rounded-lg border border-border"
                          onError={(e) =>
                            (e.currentTarget.src = "/placeholder.svg")
                          }
                        />
                      )}

                      {item.videoUrl && (
                        <YouTubeEmbed
                          url={item.videoUrl}
                          title={item.title}
                        />
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      <span>Category: {item.category}</span>
                      <span> • Status: {item.status}</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <Link to={`/learning/${item.slug || item.id}`} className="inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline">
                        Details
                      </Link>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleShareLearning(item);
                        }}
                        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                      >
                        <Share2 size={14} />
                        Share
                      </button>
                      {item.resourceUrl && (
                        <a
                          href={item.resourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground px-4 py-2 rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity"
                        >
                          Start Learning
                        </a>
                      )}
                    </div>
                  </div>

                </div>
              </motion.div>
            ))}
          </div>
        )}
        {filtered.length > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                disabled={safePage === 1}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 rounded-lg text-xs border ${
                    safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Learning;
